import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AuthContext = createContext(null);

function getToken() {
  try { return localStorage.getItem('auth_token'); } catch { return null; }
}
function setToken(token) {
  try { localStorage.setItem('auth_token', token); }
  catch (err) {
    if (process.env.NODE_ENV === 'development') console.warn('localStorage.setItem unavailable:', err);
  }
}
function clearToken() {
  try { localStorage.removeItem('auth_token'); }
  catch (err) {
    if (process.env.NODE_ENV === 'development') console.warn('localStorage.removeItem unavailable:', err);
  }
}

async function authFetch(url, options = {}) {
  try {
    const token = getToken();
    const headers = { ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(url, { ...options, headers });
    let data;
    try {
      const text = await res.text();
      data = JSON.parse(text);
    } catch {
      // Non-JSON response → almost always means the backend is down, returned a
      // gateway error (502/503), or a crash HTML page. Surface the HTTP status
      // so the user and engineering team can actually debug it.
      data = { detail: `Server error (HTTP ${res.status}). The backend returned a non-JSON response. Try again in 30 seconds or contact support.` };
    }
    return { ok: res.ok, data };
  } catch (err) {
    if (process.env.NODE_ENV === 'development') console.error('Auth fetch error:', err);
    return { ok: false, data: { detail: `Network error: ${err.message || 'could not reach server'}. Check your internet connection.` } };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const token = getToken();
    if (!token) { setLoading(false); return; }
    
    const { ok, data } = await authFetch(`${API_URL}/api/auth/me`);
    if (ok) {
      setUser(data);
    } else {
      clearToken();
    }
    setLoading(false);
    return ok ? data : null;
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { ok, data } = await authFetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!ok) throw new Error(data.detail || 'Login failed');
    setToken(data.token);
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { ok, data } = await authFetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    if (!ok) throw new Error(data.detail || 'Registration failed');
    setToken(data.token);
    setUser(data);
    return data;
  }, []);

  const googleAuth = useCallback(async (sessionId) => {
    const { ok, data } = await authFetch(`${API_URL}/api/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    if (!ok) throw new Error('Google auth failed');
    setToken(data.token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authFetch(`${API_URL}/api/auth/logout`, { method: 'POST' });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Logout error:', err);
    }
    clearToken();
    setUser(null);
  }, []);

  const addVehicle = useCallback(async (vehicleData) => {
    const { ok, data } = await authFetch(`${API_URL}/api/auth/vehicles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vehicleData),
    });
    if (!ok) throw new Error('Failed to add vehicle');
    setUser(prev => prev ? { ...prev, vehicles: [...(prev.vehicles || []), data] } : prev);
    return data;
  }, []);

  const removeVehicle = useCallback(async (vehicleId) => {
    const { ok } = await authFetch(`${API_URL}/api/auth/vehicles/${vehicleId}`, { method: 'DELETE' });
    if (!ok && process.env.NODE_ENV === 'development') console.error('Remove vehicle failed');
    setUser(prev => prev ? { ...prev, vehicles: (prev.vehicles || []).filter(v => v.vehicle_id !== vehicleId) } : prev);
  }, []);

  const value = useMemo(() => ({
    user, loading, login, register, googleAuth, logout, addVehicle, removeVehicle, checkAuth,
    refreshUser: checkAuth,
  }), [user, loading, login, register, googleAuth, logout, addVehicle, removeVehicle, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
